import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  CardMedia,
  Breadcrumbs,
  Skeleton,
  useTheme,
  alpha,
  Chip,
  Button,
} from "@mui/material";
import {
  Home,
  NavigateNext,
  AccessTime,
  Visibility,
  ArrowForward,
  HomeOutlined,
} from "@mui/icons-material";
import { Helmet } from "react-helmet";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import { useHistory } from "react-router-dom";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useCustomTheme } from "../../utils/useCustomTheme";

/* ─────────────────────────────────────────
   Skeleton loader
───────────────────────────────────────── */
const PageSkeleton = () => (
  <Container maxWidth="md" sx={{ py: 6 }}>
    <Stack spacing={1.5} mb={5}>
      <Skeleton
        variant="text"
        width={180}
        height={20}
        sx={{ borderRadius: "6px" }}
      />
    </Stack>
    <Stack alignItems="center" spacing={2.5} mb={6}>
      <Skeleton
        variant="rounded"
        width={80}
        height={80}
        sx={{ borderRadius: "16px" }}
      />
      <Skeleton
        variant="text"
        width={320}
        height={44}
        sx={{ borderRadius: "8px" }}
      />
      <Skeleton
        variant="text"
        width={180}
        height={20}
        sx={{ borderRadius: "6px" }}
      />
    </Stack>
    <Skeleton
      variant="rounded"
      height={2}
      sx={{ borderRadius: "2px", mb: 5 }}
    />
    <Stack spacing={1.5}>
      {[...Array(6)].map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          height={20}
          width={i % 3 === 2 ? "72%" : "100%"}
          sx={{ borderRadius: "6px" }}
        />
      ))}
    </Stack>
  </Container>
);

/* ─────────────────────────────────────────
   404 state
───────────────────────────────────────── */
const NotFound = ({ lang, isDark }) => {
  const theme = useTheme();
  const history = useHistory();
  const p = theme.palette.primary;

  return (
    <Box
      sx={{
        minHeight: "70vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        sx={{
          textAlign: "center",
          maxWidth: 420,
          animation: "fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
          "@keyframes fadeUp": {
            from: { opacity: 0, transform: "translateY(20px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
        }}
      >
        {/* Big 404 */}
        <Typography
          sx={{
            fontSize: { xs: "6rem", md: "8rem" },
            fontWeight: 900,
            letterSpacing: -4,
            lineHeight: 1,
            mb: 2,
            background: `linear-gradient(135deg, ${alpha(p.main, 0.25)}, ${alpha(p.light, 0.15)})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          404
        </Typography>

        {/* Icon badge */}
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: "16px",
            bgcolor: alpha(p.main, isDark ? 0.12 : 0.08),
            border: `1px solid ${alpha(p.main, isDark ? 0.2 : 0.12)}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <HomeOutlined sx={{ fontSize: 24, color: "primary.main" }} />
        </Box>

        <Typography
          fontWeight={800}
          fontSize={{ xs: "1.3rem", md: "1.5rem" }}
          letterSpacing={-0.5}
          color="text.primary"
          mb={1}
        >
          {lang?.pageNotFound || "Page not found"}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          lineHeight={1.75}
          fontSize={14}
          mb={4}
        >
          {lang?.pageNotFoundDesc ||
            "The page you're looking for doesn't exist or has been moved."}
        </Typography>

        <Button
          onClick={() => history.push("/")}
          variant="contained"
          disableElevation
          endIcon={
            <ArrowForward
              sx={{
                fontSize: "14px !important",
                transition: "transform 0.2s ease",
              }}
            />
          }
          sx={{
            borderRadius: "12px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: 14,
            px: 3,
            py: 1.3,
            bgcolor: "primary.main",
            boxShadow: `0 4px 16px ${alpha(p.main, 0.28)}`,
            transition: "all 0.22s ease",
            "&:hover": {
              bgcolor: "primary.dark",
              boxShadow: `0 8px 28px ${alpha(p.main, 0.42)}`,
              transform: "translateY(-1px)",
              "& .MuiButton-endIcon": { transform: "translateX(3px)" },
            },
            "&:active": { transform: "translateY(0)" },
          }}
        >
          {lang?.backToHome || "Back to home"}
        </Button>
      </Box>
    </Box>
  );
};

/* ─────────────────────────────────────────
   Main component
───────────────────────────────────────── */
const ViewPage = (props) => {
  const slug = props.match.params.slug;
  const theme = useTheme();
  const history = useHistory();
  const { hitAxios } = useContext(GlobalContext);
  const { lang } = useContext(TranslateContext);
  const { toggleColorMode, isDark } = useCustomTheme();
  const p = theme.palette.primary;

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  async function getPage() {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await hitAxios({
        path: "/api/web/get_page_slug",
        post: true,
        admin: true,
        obj: { slug },
      });
      if (!res.data.page) {
        setNotFound(true);
      } else {
        setPage(res.data.data);
      }
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!slug) return;
    window.scrollTo(0, 0);
    getPage();
  }, [slug]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {page
            ? page.title
            : notFound
              ? lang?.pageNotFound || "Page Not Found"
              : "Loading..."}
        </title>
        {page?.title && <meta name="description" content={page.title} />}
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <Navbar onToggleTheme={toggleColorMode} isDark={isDark} theme={theme} />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          pt: { xs: "64px", md: "72px" }, // offset for fixed navbar
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* ── Background glows ── */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: "-10%",
              left: "50%",
              transform: "translateX(-50%)",
              width: "70%",
              height: "40%",
              borderRadius: "50%",
              background: `radial-gradient(ellipse, ${alpha(p.main, isDark ? 0.08 : 0.05)} 0%, transparent 65%)`,
              filter: "blur(80px)",
            }}
          />
          {/* Dot grid */}
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              opacity: isDark ? 0.03 : 0.018,
              backgroundImage: `radial-gradient(${theme.palette.text.primary} 1px, transparent 1px)`,
              backgroundSize: "28px 28px",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `radial-gradient(ellipse 90% 80% at 50% 50%, transparent 30%, ${theme.palette.background.default} 100%)`,
            }}
          />
        </Box>

        {/* ── Content ── */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          {loading ? (
            <Box
              sx={{
                animation: "fadeIn 0.3s ease both",
                "@keyframes fadeIn": {
                  from: { opacity: 0 },
                  to: { opacity: 1 },
                },
              }}
            >
              <PageSkeleton />
            </Box>
          ) : notFound ? (
            <NotFound lang={lang} isDark={isDark} />
          ) : (
            <Box
              sx={{
                animation: "fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both",
                "@keyframes fadeUp": {
                  from: { opacity: 0, transform: "translateY(16px)" },
                  to: { opacity: 1, transform: "translateY(0)" },
                },
              }}
            >
              <Container maxWidth="md" sx={{ py: { xs: 5, md: 7 } }}>
                {/* ── Breadcrumbs ── */}
                <Breadcrumbs
                  separator={
                    <NavigateNext
                      sx={{ fontSize: 14, color: "text.disabled" }}
                    />
                  }
                  sx={{ mb: 5 }}
                >
                  <Box
                    onClick={() => history.push("/")}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      cursor: "pointer",
                      color: "text.disabled",
                      fontSize: 13,
                      fontWeight: 500,
                      transition: "color 0.2s",
                      "&:hover": { color: "primary.main" },
                    }}
                  >
                    <Home sx={{ fontSize: 14 }} />
                    {lang?.home || "Home"}
                  </Box>
                  <Typography
                    fontSize={13}
                    fontWeight={600}
                    color="text.secondary"
                    sx={{
                      maxWidth: 220,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {page.title}
                  </Typography>
                </Breadcrumbs>

                {/* ── Page header ── */}
                <Box
                  sx={{
                    textAlign: "center",
                    mb: 6,
                    pb: 6,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {/* Title */}
                  <Typography
                    component="h1"
                    fontWeight={800}
                    letterSpacing={-1}
                    color="text.primary"
                    sx={{
                      fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.6rem" },
                      lineHeight: 1.15,
                      mb: 2.5,
                    }}
                  >
                    {page.title}
                  </Typography>

                  {/* Meta chips */}
                  {(page.createdAt || page.viewCount) && (
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="center"
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {page.createdAt && (
                        <Chip
                          icon={
                            <AccessTime
                              sx={{
                                fontSize: "13px !important",
                                color: `${alpha(p.main, 0.7)} !important`,
                              }}
                            />
                          }
                          label={formatDate(page.createdAt)}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: 12,
                            fontWeight: 500,
                            bgcolor: alpha(p.main, isDark ? 0.1 : 0.07),
                            color: "text.secondary",
                            border: `1px solid ${alpha(p.main, isDark ? 0.18 : 0.1)}`,
                            borderRadius: "8px",
                          }}
                        />
                      )}
                      {page.viewCount && (
                        <Chip
                          icon={
                            <Visibility
                              sx={{
                                fontSize: "13px !important",
                                color: `${alpha(theme.palette.secondary.main, 0.7)} !important`,
                              }}
                            />
                          }
                          label={`${page.viewCount} ${lang?.views || "views"}`}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: 12,
                            fontWeight: 500,
                            bgcolor: alpha(
                              theme.palette.secondary.main,
                              isDark ? 0.1 : 0.07,
                            ),
                            color: "text.secondary",
                            border: `1px solid ${alpha(
                              theme.palette.secondary.main,
                              isDark ? 0.18 : 0.1,
                            )}`,
                            borderRadius: "8px",
                          }}
                        />
                      )}
                    </Stack>
                  )}
                </Box>

                {/* ── Page content ── */}
                <Box
                  sx={{
                    fontSize: { xs: "1rem", md: "1.05rem" },
                    lineHeight: 1.85,
                    color: "text.primary",
                    /* Rich text styles */
                    "& img": {
                      maxWidth: "100%",
                      height: "auto",
                      borderRadius: "12px",
                      my: 2.5,
                      display: "block",
                      boxShadow: isDark
                        ? `0 4px 20px ${alpha(theme.palette.common.black, 0.35)}`
                        : `0 2px 12px ${alpha(theme.palette.common.black, 0.08)}`,
                    },
                    "& a": {
                      color: "primary.main",
                      textDecoration: "none",
                      fontWeight: 500,
                      "&:hover": { textDecoration: "underline" },
                    },
                    "& h1,& h2,& h3,& h4,& h5,& h6": {
                      color: "text.primary",
                      fontWeight: 700,
                      letterSpacing: -0.3,
                      mt: 4,
                      mb: 1.5,
                    },
                    "& h1": { fontSize: "1.8rem" },
                    "& h2": { fontSize: "1.5rem" },
                    "& h3": { fontSize: "1.25rem" },
                    "& p": { mb: 2 },
                    "& blockquote": {
                      borderLeft: `3px solid ${p.main}`,
                      pl: 2.5,
                      py: 0.5,
                      my: 3,
                      bgcolor: alpha(p.main, isDark ? 0.07 : 0.04),
                      borderRadius: "0 10px 10px 0",
                      color: "text.secondary",
                      fontStyle: "italic",
                    },
                    "& ul,& ol": { pl: 3, mb: 2 },
                    "& li": { mb: 0.75 },
                    "& table": {
                      width: "100%",
                      borderCollapse: "collapse",
                      my: 3,
                      fontSize: "0.9rem",
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: "10px",
                      overflow: "hidden",
                    },
                    "& th,& td": {
                      border: `1px solid ${theme.palette.divider}`,
                      p: "10px 14px",
                      textAlign: "left",
                    },
                    "& th": {
                      bgcolor: alpha(p.main, isDark ? 0.1 : 0.06),
                      fontWeight: 700,
                      fontSize: "0.85rem",
                      letterSpacing: 0.2,
                    },
                    "& code": {
                      fontFamily: "monospace",
                      fontSize: "0.88em",
                      bgcolor: alpha(p.main, isDark ? 0.1 : 0.06),
                      color: "primary.main",
                      px: 0.75,
                      py: 0.25,
                      borderRadius: "5px",
                    },
                    "& pre": {
                      bgcolor: isDark
                        ? alpha(theme.palette.background.paper, 0.8)
                        : alpha(theme.palette.background.paper, 1),
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: "12px",
                      p: 2.5,
                      overflowX: "auto",
                      my: 2.5,
                      "& code": {
                        bgcolor: "transparent",
                        color: "text.primary",
                        p: 0,
                      },
                    },
                    "& hr": {
                      border: "none",
                      borderTop: `1px solid ${theme.palette.divider}`,
                      my: 3,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: page.value }}
                />

                {/* ── Tags ── */}
                {page.tags?.length > 0 && (
                  <Box
                    sx={{
                      mt: 6,
                      pt: 4,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      fontSize={12}
                      color="text.disabled"
                      letterSpacing={0.8}
                      display="block"
                      mb={1.5}
                    >
                      {lang?.relatedTags || "TAGS"}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {page.tags.map((tag, i) => (
                        <Chip
                          key={i}
                          label={tag}
                          size="small"
                          sx={{
                            height: 26,
                            fontSize: 12,
                            fontWeight: 500,
                            borderRadius: "8px",
                            bgcolor: alpha(p.main, isDark ? 0.1 : 0.07),
                            color: "text.secondary",
                            border: `1px solid ${alpha(p.main, isDark ? 0.18 : 0.1)}`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                              bgcolor: alpha(p.main, isDark ? 0.18 : 0.12),
                              color: "primary.main",
                            },
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Container>
            </Box>
          )}
        </Box>
      </Box>
      <Footer theme={theme} />
    </>
  );
};

export default ViewPage;
